/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SxProps } from "@mui/material";
import { GridCallbackDetails, GridRowParams, GridSortModel, MuiEvent } from "@mui/x-data-grid";
import { useAuditTrailData } from "./hooks";
import AuditFilter from "./AuditFilter/AuditFilter";

import DateRangeCalendarPicker from "../../Shared/Calendar/DateRangeCalendarPicker";
import { useStoreDispatch } from "../../../store/hooks";
import { setSelectedFromDate, setSelectedToDate } from "../../../store/filters/audit/reducers";
import { useSelector } from "react-redux";
import { auditFilterFromDateSelector, auditFilterToDateSelector } from "../../../store/filters/audit/selectors";
import dayjs, { Dayjs } from "dayjs";
import CustomDataGrid from "../../Shared/Table/CustomDataGrid";

import { IAuditTrail } from "../../../store/api/admin/types";
import AuditDetailsModal from "./AuditDetailsModal";
import { Loader2 } from "lucide-react";
import { Icon } from "../../Shared/Icon";
import { Button } from "../../ui/button";
import { useAuditFilter } from "./AuditFilter/hooks";
import { resetAuditFilters } from "../../../store/filters/audit/reducers";
import { searchKeywordSelector } from "../../../store/search/selector";
import { resetKeyword } from "../../../store/search/reducer";
import ActiveFilterChips from "../../Shared/Filter/ActiveFilterChips";
import { RadixSelectControl } from "../../ui/radix-select";

const TIME_FILTER_OPTIONS = [
  { value: "__placeholder__", label: "Date Range" },
  { value: "today", label: "Today" },
  { value: "last7days", label: "Last 7 Days" },
  { value: "last30days", label: "Last 30 Days" },
  { value: "custom", label: "Custom" },
];

const AuditTrailDetails: React.FC = () => {
  const dispatch = useStoreDispatch();
  const [rowDetails, setRowDetails] = useState<IAuditTrail>();
  const [selectedTimeFilter, setSelectedTimeFilter] = useState<string>("__placeholder__");
  const [isCustomDatePopoverOpen, setIsCustomDatePopoverOpen] = useState(false);
  const timeFilterAnchorRef = useRef<HTMLDivElement | null>(null);
  const { data, isLoading, auditTrailColumns, handleGridSort, exportHandler, exportStatus } = useAuditTrailData();
  const selectedFromDate = useSelector(auditFilterFromDateSelector);
  const selectedToDate = useSelector(auditFilterToDateSelector);
  const searchKeyword = useSelector(searchKeywordSelector);
  const { auditFilters } = useAuditFilter();

  const yesterday = dayjs().subtract(1, "day");

  const allFilters = useMemo(() => {
    const filters = [...auditFilters];
    if (selectedFromDate) {
      filters.push({
        id: "fromDate",
        label: "From",
        value: dayjs(selectedFromDate).format("DD-MMM-YY HH:mm"),
        setValue: setSelectedFromDate as any,
        values: [],
      });
    }
    if (selectedToDate) {
      filters.push({
        id: "toDate",
        label: "To",
        value: dayjs(selectedToDate).format("DD-MMM-YY HH:mm"),
        setValue: setSelectedToDate as any,
        values: [],
      });
    }
    return filters;
  }, [auditFilters, selectedFromDate, selectedToDate]);

  const handleClearSearch = useCallback(() => {
    dispatch(resetKeyword());
  }, [dispatch]);

  const handleClearAllFilters = useCallback(() => {
    dispatch(resetAuditFilters());
    dispatch(resetKeyword());
    setSelectedTimeFilter("__placeholder__");
  }, [dispatch]);

  const rowsData = data?.data || [];
  const noOfRows = data?.totalItems || 0;
  const dataGridProps: SxProps = {
    height: window.innerHeight - 150,
  };
  const handleSortModelChange = React.useCallback(
    (sortModel: GridSortModel) => {
      if (sortModel.length > 0) {
        handleGridSort(sortModel);
      }
    },
    [handleGridSort]
  );

  useEffect(() => {
    if (!selectedFromDate && !selectedToDate && selectedTimeFilter && selectedTimeFilter !== "__placeholder__") {
      setSelectedTimeFilter("__placeholder__");
    }
  }, [selectedFromDate, selectedToDate]);

  const handleTimeFilterChange = (value: string) => {
    if (value === "__placeholder__") {
      setSelectedTimeFilter("__placeholder__");
      setIsCustomDatePopoverOpen(false);
      dispatch(setSelectedFromDate(null));
      dispatch(setSelectedToDate(null));
      return;
    }
    setSelectedTimeFilter(value);
    if (value === "custom") {
      // Defer popover open to next frame so Radix Select portal fully unmounts first
      requestAnimationFrame(() => setIsCustomDatePopoverOpen(true));
    } else {
      setIsCustomDatePopoverOpen(false);
      if (value === "today") {
        dispatch(setSelectedFromDate(dayjs().startOf("day")));
        dispatch(setSelectedToDate(dayjs()));
      } else if (value === "last7days") {
        dispatch(setSelectedFromDate(dayjs().subtract(7, "day")));
        dispatch(setSelectedToDate(dayjs()));
      } else if (value === "last30days") {
        dispatch(setSelectedFromDate(dayjs().subtract(30, "day")));
        dispatch(setSelectedToDate(dayjs()));
      }
    }
  };

  const handalOnChangeFromDate = (dateTime: Dayjs | null | undefined) => {
    dispatch(setSelectedFromDate(dateTime));
  };
  const handalOnChangeToDate = (dateTime: Dayjs | null | undefined) => {
    dispatch(setSelectedToDate(dateTime));
  };
  const rowClickHandler = (params: GridRowParams, event: MuiEvent, details: GridCallbackDetails) => {
    setRowDetails(params.row);
  };
  // const tomorrow = dayjs().add(1, "day");

  return (
    <>
      {!!rowDetails && <AuditDetailsModal rowDetails={rowDetails} setRowDetails={setRowDetails} />}
      <div className="mt-3 mb-1 w-full flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <AuditFilter>
            <div className="flex items-center gap-2 ml-2">
              <div ref={timeFilterAnchorRef}>
                <RadixSelectControl
                  value={selectedTimeFilter}
                  onValueChange={handleTimeFilterChange}
                  onItemSelect={(val) => {
                    if (val === "custom") {
                      requestAnimationFrame(() => setIsCustomDatePopoverOpen(true));
                    }
                  }}
                  options={TIME_FILTER_OPTIONS}
                  placeholder="Date Range"
                  className="font-medium !text-[#0F172A] dark:!text-[#FFFFFF]"
                  selectedLabelOverride={
                    selectedTimeFilter === "custom" && selectedFromDate && selectedToDate
                      ? `${dayjs(selectedFromDate).format("MMM D, YYYY")} – ${dayjs(selectedToDate).format("MMM D, YYYY")}`
                      : undefined
                  }
                  contentWidth="170px"
                />
              </div>
              <DateRangeCalendarPicker
                fromValue={selectedFromDate || yesterday}
                toValue={selectedToDate || dayjs()}
                onFromChange={handalOnChangeFromDate}
                onToChange={handalOnChangeToDate}
                hideTrigger
                open={isCustomDatePopoverOpen}
                anchorEl={timeFilterAnchorRef.current}
                onClose={() => setIsCustomDatePopoverOpen(false)}
              />
            </div>
          </AuditFilter>
        </div>
        <div className="shrink-0 flex items-center justify-end">
          <Button
            variant="outline"
            size="sm"
            className="shrink-0 gap-1.5"
            disabled={exportStatus.isLoading}
            onClick={exportHandler}
            aria-label="Export"
          >
            {exportStatus.isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon name="download" size={15} />}
            <span className="text-xs font-medium">Export</span>
          </Button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-1 mt-8 w-full">
       <span className="text-sm text-muted-foreground">{`${rowsData?.length || 0} results`}</span>    
        <ActiveFilterChips
          filters={allFilters}
          searchKeyword={searchKeyword}
          onClearSearch={handleClearSearch}
          onClearAll={handleClearAllFilters}
          dispatch={dispatch}
        />
      </div>
        <CustomDataGrid
          tableId="auditTrail"
          disableColumnMenu
          sx={{
            "& .MuiDataGrid-columnHeaderTitle": {
              fontSize: "0.9rem",
            },
          }}
          rows={rowsData}
          columns={auditTrailColumns}
          checkboxSelection={false}
          rowCount={noOfRows}
          loading={isLoading}
          onSortModelChange={handleSortModelChange}
          onRowClick={rowClickHandler}
          fillContainer
        />
    </>
  );
};

export default AuditTrailDetails;